import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import UserAvatar, { type UserAvatarProps } from '@/components/common/user-carousel/user-avatar';
import ROUTES from '@/routes';
import { useAuthStore } from '@/stores/auth';
import { addToast } from '@/stores/toast';
import cn from '@/utils/merge-class-names';

export type UserCarouselItem = UserAvatarProps & { userId: number; groupId?: number };

interface Props {
  users: Array<UserCarouselItem>;
  withLink?: boolean;
  className?: string;
  onRemove?: (userId: number) => void;
}

export default function UserCarousel({ users, withLink, children, className, onRemove }: PropsWithChildren<Props>) {
  const { t } = useTranslation();
  const selfUserId = useAuthStore((s) => s.auth?.UserID);

  function handleClickSelf() {
    addToast({
      type: 'info',
      message: t('conversation.setting.group.admin.operate-self'),
    });
  }

  function handleRemove(userId: number) {
    if (onRemove) {
      return () => onRemove(userId);
    }
    return undefined;
  }

  return (
    <div className={cn('flex gap-x-4 overflow-x-auto overflow-y-hidden py-4', className)}>
      {children}

      {users.map((user) =>
        withLink ? (
          user.userId === selfUserId ? (
            <button
              key={user.userId}
              type='button'
              onClick={handleClickSelf}>
              <UserAvatar
                className='size-16 overflow-hidden px-1'
                {...user}
                onRemove={handleRemove(user.userId)}
              />
            </button>
          ) : (
            <Link
              key={user.userId}
              to={ROUTES.CONTACT.USERS.BY_ID.$buildPath({
                params: { userId: user.userId },
                searchParams: { groupId: user.groupId },
              })}>
              <UserAvatar
                {...user}
                className='size-16 overflow-hidden px-1'
                onRemove={handleRemove(user.userId)}
              />
            </Link>
          )
        ) : (
          <UserAvatar
            className='size-16 overflow-hidden px-1'
            key={user.userId}
            {...user}
            onRemove={user.userId === selfUserId ? undefined : handleRemove(user.userId)}
          />
        ),
      )}
    </div>
  );
}
